import dayjs, { type Dayjs } from 'dayjs'

import { resolveTimezone } from '@/shared/lib/resolve-timezone'

export const normalizeAlarmDateString = (date: string): string => {
  if (!date) {
    return date
  }

  const trimmed = date.trim()
  if (trimmed.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.substring(0, 10)
  }

  const parsed = dayjs(trimmed)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : trimmed
}

export const resolveAlarmUserTimezone = (preference?: string | null): string => {
  return resolveTimezone(preference ?? undefined)
}

export const parseServerAlarmUtc = (date: string, time: string, tzOffsetMinutes: number): Dayjs => {
  return dayjs(`${date} ${time}`)
    .utc(true)
    .subtract(tzOffsetMinutes, 'minute')
}

export const formatServerAlarmFields = (
  momentUtc: Dayjs,
  tzOffsetMinutes: number,
): { date: string, time: string } => {
  const shifted = momentUtc.utc().add(tzOffsetMinutes, 'minute')

  return {
    date: shifted.format('YYYY-MM-DD'),
    time: shifted.format('HH:mm'),
  }
}

export const parseUserAlarmInput = (date: string, time: string, userTimezone: string): Dayjs => {
  const tz = resolveAlarmUserTimezone(userTimezone)

  return dayjs.tz(`${date} ${time}`, tz)
    .second(0)
    .millisecond(0)
}

export const formatUserAlarmDisplay = (
  momentUtc: Dayjs,
  userTimezone: string,
): { date: string, time: string } => {
  const inUserTz = momentUtc.tz(resolveAlarmUserTimezone(userTimezone))

  return {
    date: inUserTz.format('YYYY-MM-DD'),
    time: inUserTz.format('HH:mm'),
  }
}

export const getAlarmUnixTimestamp = (date: string, time: string, tzOffsetMinutes: number): number => {
  return parseServerAlarmUtc(date, time, tzOffsetMinutes).unix()
}

export const getDefaultAlarmDateTimeForCalendarDay = (dateYYYYMMDD: string, userTimezone: string): Dayjs => {
  const tz = resolveAlarmUserTimezone(userTimezone)
  const day = dayjs.tz(dateYYYYMMDD, tz)
  const now = dayjs.tz(undefined, tz)

  if (day.isSame(now, 'day')) {
    return now.second(0).millisecond(0)
  }

  return day.hour(12).minute(0).second(0).millisecond(0)
}
